import React, { useState, useEffect } from 'react';
import ChildSwitcher from './ChildSwitcher';
import FeeChallan from './FeeChallan';
import { useMyChildren } from '../hooks/useMyChildren';
import api from '../api/axios';
import { CURRENCY } from '../config';
import { buildStatement } from '../utils/challan';

function downloadCSV(filename, headerRow, dataRows) {
  const rows = [headerRow, ...dataRows];
  const csv = rows
    .map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function deriveStatus(fee) {
  const balance = (fee.amount || 0) - (fee.paid_amount || 0);
  if (balance <= 0.01) return 'Paid';
  if (fee.due_date && new Date(fee.due_date) < new Date()) return 'Overdue';
  return 'Pending';
}

export default function ChildPayments() {
  const { children, selectedChild, setSelectedChild, loading } = useMyChildren();
  const [fees, setFees] = useState([]);
  const [structures, setStructures] = useState([]);
  const [feesLoading, setFeesLoading] = useState(false);
  const [monthFilter, setMonthFilter] = useState('all');
  const [challanFee, setChallanFee] = useState(null);

  useEffect(() => {
    if (!selectedChild?.student_id) { setFees([]); return; }
    setFeesLoading(true);
    Promise.all([
      api.get('/fees/'),
      // Public fee-structure endpoint (must exist — see backend note below).
      api.get('/fees/public-structures').catch(() => ({ data: [] })),
    ])
      .then(([feesRes, structRes]) => {
        const all = feesRes.data || [];
        setFees(all.filter(f => f.student_id === selectedChild.student_id));
        setStructures(structRes.data || []);
      })
      .catch(() => setFees([]))
      .finally(() => setFeesLoading(false));
  }, [selectedChild?.student_id]);

  if (loading) {
    return <div className="bg-white p-8 rounded shadow text-center text-gray-500">Loading…</div>;
  }

  if (children.length === 0) {
    return (
      <div className="bg-white p-8 rounded shadow text-center text-gray-400">
        <i className="fas fa-inbox text-4xl mb-3"></i>
        <p>No children linked to your account.</p>
      </div>
    );
  }

  const monthsSet = new Set();
  fees.forEach(f => { if (f.due_date) monthsSet.add(f.due_date.substring(0, 7)); });
  const months = [...monthsSet].sort().reverse();

  const filtered = monthFilter === 'all'
    ? fees
    : fees.filter(f => f.due_date && f.due_date.startsWith(monthFilter));

  const totalBilled = filtered.reduce((s, f) => s + (f.amount || 0), 0);
  const totalPaid = filtered.reduce((s, f) => s + (f.paid_amount || 0), 0);
  const totalBalance = totalBilled - totalPaid;

  const handleDownload = () => {
    const rows = filtered.map(f => [
      f.due_date || '',
      f.fee_type || '',
      f.amount ?? '',
      f.paid_amount ?? 0,
      ((f.amount || 0) - (f.paid_amount || 0)).toFixed(2),
      deriveStatus(f),
      f.paid_date || '',
      f.payment_method || '',
    ]);
    downloadCSV(
      `payments_${selectedChild.first_name}_${monthFilter}.csv`,
      ['Due Date', 'Type', 'Amount', 'Paid', 'Balance', 'Status', 'Paid Date', 'Method'],
      rows,
    );
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-4 text-gray-800">
        <i className="fas fa-money-bill-wave text-blue-500 mr-2"></i>Payments
      </h2>

      <ChildSwitcher
        children={children}
        selectedChild={selectedChild}
        onSelect={setSelectedChild}
      />

      {feesLoading ? (
        <div className="bg-white p-8 rounded shadow text-center text-gray-500">Loading payments…</div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white p-3 sm:p-4 rounded shadow flex flex-wrap gap-3 items-center">
            <label className="text-sm font-medium text-gray-700">Month:</label>
            <select value={monthFilter}
              onChange={e => setMonthFilter(e.target.value)}
              className="p-2 border rounded text-sm flex-1 min-w-[180px]">
              <option value="all">All months</option>
              {months.map(m => (
                <option key={m} value={m}>
                  {new Date(m + '-01').toLocaleDateString('en', { month: 'long', year: 'numeric' })}
                </option>
              ))}
            </select>
            <button onClick={handleDownload} disabled={filtered.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50">
              <i className="fas fa-download mr-1"></i> Download CSV
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white p-3 sm:p-4 rounded shadow border-l-4 border-blue-500">
              <p className="text-xs text-gray-500">Total Billed</p>
              <p className="text-lg sm:text-xl font-bold text-blue-700">{CURRENCY}{totalBilled.toFixed(2)}</p>
            </div>
            <div className="bg-white p-3 sm:p-4 rounded shadow border-l-4 border-green-500">
              <p className="text-xs text-gray-500">Total Paid</p>
              <p className="text-lg sm:text-xl font-bold text-green-700">{CURRENCY}{totalPaid.toFixed(2)}</p>
            </div>
            <div className="bg-white p-3 sm:p-4 rounded shadow border-l-4 border-red-500">
              <p className="text-xs text-gray-500">Balance</p>
              <p className="text-lg sm:text-xl font-bold text-red-700">{CURRENCY}{totalBalance.toFixed(2)}</p>
            </div>
          </div>

          <div className="bg-white rounded shadow overflow-hidden">
            {filtered.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <i className="fas fa-inbox text-4xl mb-3"></i>
                <p>No payments for this period.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-3 text-left font-semibold text-gray-700">Due Date</th>
                      <th className="p-3 text-left font-semibold text-gray-700">Type</th>
                      <th className="p-3 text-right font-semibold text-gray-700">Amount</th>
                      <th className="p-3 text-right font-semibold text-gray-700">Paid</th>
                      <th className="p-3 text-right font-semibold text-gray-700">Balance</th>
                      <th className="p-3 text-center font-semibold text-gray-700">Status</th>
                      <th className="p-3 text-center font-semibold text-gray-700">Challan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(f => {
                      const balance = (f.amount || 0) - (f.paid_amount || 0);
                      const displayStatus = deriveStatus(f);
                      const isPaid = displayStatus === 'Paid';
                      return (
                        <tr key={f.id} className="border-t hover:bg-gray-50">
                          <td className="p-3">{f.due_date}</td>
                          <td className="p-3">{f.fee_type}</td>
                          <td className="p-3 text-right">{CURRENCY}{f.amount}</td>
                          <td className="p-3 text-right text-green-700">{CURRENCY}{f.paid_amount || 0}</td>
                          <td className="p-3 text-right text-red-700 font-semibold">{CURRENCY}{balance.toFixed(2)}</td>
                          <td className="p-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              displayStatus === 'Paid' ? 'bg-green-100 text-green-800'
                              : displayStatus === 'Overdue' ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'}`}>
                              {displayStatus}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <button
                              onClick={() => setChallanFee(f)}
                              className={`px-3 py-1 text-xs rounded font-semibold whitespace-nowrap ${
                                isPaid
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                  : 'bg-blue-100 text-blue-800 hover:bg-blue-200'}`}>
                              <i className={`fas ${isPaid ? 'fa-check-circle' : 'fa-file-invoice'} mr-1`}></i>
                              {isPaid ? 'Receipt' : 'Statement'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {challanFee && (
        <FeeChallan
          fee={challanFee}
          child={selectedChild}
          student={{ id: selectedChild.student_id, class_id: selectedChild.class_id }}
          allFees={fees}
          structures={structures}
          onClose={() => setChallanFee(null)}
        />
      )}
    </div>
  );
}